import { useState } from "react";
import { buildProformaHtml, downloadProformaPdf, attachProformaToPratica, proformaPdfBlob } from "../lib/proformaPdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, Mail, ShieldCheck, Loader2, FileText, Paperclip, Printer, X } from "lucide-react";
import { toast } from "sonner";

export default function ProformaPreviewDialog({ open, onOpenChange, proforma, studio, onEmail, onPec }) {
  const [busy, setBusy] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [printing, setPrinting] = useState(false);
  if (!proforma) return null;

  const dl = async () => {
    setBusy(true);
    try { await downloadProformaPdf(proforma, studio); }
    catch { toast.error("Generazione PDF non riuscita"); }
    finally { setBusy(false); }
  };

  const stampa = async () => {
    setPrinting(true);
    try {
      const blob = await proformaPdfBlob(proforma, studio);
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) { w.addEventListener("load", () => { try { w.focus(); w.print(); } catch (e) { /* noop */ } }); }
      else { toast.error("Consenti i popup per stampare"); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Stampa non riuscita"); }
    finally { setPrinting(false); }
  };

  const attach = async () => {
    setAttaching(true);
    try { await attachProformaToPratica(proforma, studio); toast.success("PDF allegato alla pratica (Documenti in uscita)"); }
    catch (e) { toast.error("Allegato alla pratica non riuscito"); }
    finally { setAttaching(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="pgp-modal bg-[#121824] border-slate-700 text-slate-100 max-w-4xl overflow-y-auto" data-testid="proforma-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-400" /> Anteprima Proforma {proforma.numero}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-slate-800">
          <Button onClick={stampa} disabled={printing} data-testid="proforma-preview-print" className="bg-teal-500 hover:bg-teal-600 text-[#0a0d14] font-semibold gap-1">
            {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Stampa
          </Button>
          <Button onClick={dl} disabled={busy} variant="outline" data-testid="proforma-preview-download" className="border-slate-600 text-slate-200 gap-1">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Scarica PDF
          </Button>
          {onEmail && (
            <Button onClick={() => onEmail(proforma)} variant="outline" data-testid="proforma-preview-email" className="border-slate-600 text-slate-200 gap-1">
              <Mail className="h-4 w-4" /> Invia Email
            </Button>
          )}
          {onPec && (
            <Button onClick={() => onPec(proforma)} variant="outline" data-testid="proforma-preview-pec" className="border-slate-600 text-slate-200 gap-1">
              <ShieldCheck className="h-4 w-4" /> Invia PEC
            </Button>
          )}
          {proforma.pratica_id && (
            <Button onClick={attach} disabled={attaching} variant="outline" data-testid="proforma-preview-attach" className="border-slate-600 text-slate-200 gap-1">
              {attaching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} Allega alla pratica
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} variant="ghost" data-testid="proforma-preview-close" className="text-slate-300 gap-1 ml-auto">
            <X className="h-4 w-4" /> Chiudi
          </Button>
        </div>

        <div className="mt-3 overflow-auto bg-slate-500/20 rounded-lg p-4 flex justify-center" style={{ maxHeight: "60vh" }}>
          <div
            data-testid="proforma-preview-sheet"
            style={{ boxShadow: "0 8px 30px rgba(0,0,0,.4)", flex: "0 0 auto" }}
            dangerouslySetInnerHTML={{ __html: buildProformaHtml(proforma, studio) }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
